import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Form, FormFeedback, FormGroup, Input, Label } from "reactstrap";
import { FaArrowLeft, FaArrowRight, FaEnvelope, FaSpinner } from "react-icons/fa";
import toastr from "toastr";
import GCaptcha from "../Authentication/GCaptcha";
import OtpInput from "../Authentication/OtpInput";
import Logo from "../../assets/images/LogoNew.svg";
import { isValidEmail, normalizeEmail } from "../../util/email";
import { BrokerService, setBrokerProfile } from "./BrokerService";
import BrokerAlert from "../../components/Broker/BrokerAlert";
import { buildBrokerPath, getBrokerBasePath } from "../../components/Broker/brokerPaths";

const BrokerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [otpId, setOtpId] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [step, setStep] = useState("credentials");
  const [loading, setLoading] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [shouldByPassCaptcha, setShouldByPassCaptcha] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let isMounted = true;
    BrokerService.captchaBypassConfig()
      .then((response) => {
        if (isMounted && response?.data?.defaultOTP) {
          setShouldByPassCaptcha(true);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const refreshCaptcha = () => {
    setRecaptchaToken("");
    setCaptchaKey((value) => value + 1);
  };

  const requestOtp = async (event) => {
    event.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    setShowErrors(true);
    setPageError("");
    if (!isValidEmail(normalizedEmail)) {
      setPageError("Enter the team email FV Bank registered for your partner workspace.");
      toastr.error("Please enter a valid email address.");
      return;
    }
    if (!shouldByPassCaptcha && !recaptchaToken) {
      setPageError("Complete the captcha before requesting your verification code.");
      toastr.error("Please complete the captcha.");
      return;
    }

    setLoading(true);
    try {
      const response = await BrokerService.requestLoginOtp({
        email: normalizedEmail,
        recaptchaToken,
      });
      setEmail(normalizedEmail);
      setOtpId(response?.data?.email || "");
      setOtpValue("");
      setStep("otp");
      toastr.success("Verification code sent to your email.");
    } catch (error) {
      refreshCaptcha();
      setPageError(error?.message || "Unable to send verification code.");
      toastr.error(error?.message || "Unable to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    const normalizedEmail = normalizeEmail(email);
    setPageError("");
    if (otpValue?.length !== 4) {
      setPageError("Enter the 4-digit code sent to your email.");
      toastr.error("Please enter the 4-digit verification code.");
      return;
    }

    setLoading(true);
    try {
      const response = await BrokerService.login({
        email: normalizedEmail,
        otpId,
        otpValue,
      });
      setBrokerProfile(response?.data, normalizedEmail);
      const basePath = getBrokerBasePath(location.state?.from?.pathname || location.pathname);
      const redirectTo = location.state?.from?.pathname || buildBrokerPath(basePath, "/dashboard");
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setPageError(error?.message || "Unable to sign in.");
      toastr.error(error?.message || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="broker-login">
      <section className="broker-login__frame" aria-label="FV Bank Partner Portal sign in">
        <div className="broker-login__brand-panel">
          <div className="broker-login__logo-lockup">
            <img src={Logo} alt="FV Bank" />
          </div>
          <div className="broker-login__brand-copy">
            <p>Managed accounts onboarding</p>
            <h1>Onboard your clients to FV Bank with confidence</h1>
          </div>
        </div>

        <Card className="broker-login-card">
          <CardBody>
            <div className="broker-login-card__header">
              <span className="broker-login-step">{step === "credentials" ? "Step 1 of 2" : "Step 2 of 2"}</span>
              <h2>{step === "credentials" ? "Sign in to your workspace" : "Confirm your email"}</h2>
            </div>

            {pageError ? <BrokerAlert type="error" title="Sign-in needs attention">{pageError}</BrokerAlert> : null}

            {step === "credentials" ? (
              <Form onSubmit={requestOtp} aria-busy={loading}>
                <FormGroup className="broker-login-field">
                  <Label for="broker-email">Email address</Label>
                  <div className="broker-login-input-wrap">
                    <FaEnvelope aria-hidden="true" />
                    <Input
                      id="broker-email"
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setPageError("");
                      }}
                      autoComplete="email"
                      placeholder="name@partner.com"
                      invalid={showErrors && !isValidEmail(email)}
                      required
                    />
                  </div>
                  {showErrors && !isValidEmail(email) ? (
                    <FormFeedback className="d-block">Please enter a valid email address.</FormFeedback>
                  ) : null}
                </FormGroup>
                {!shouldByPassCaptcha ? (
                  <div className="broker-login-recaptcha">
                    <GCaptcha
                      key={captchaKey}
                      onChange={(value) => {
                        setRecaptchaToken(value);
                        setPageError("");
                      }}
                      onExpired={refreshCaptcha}
                      testId="broker-login-recaptcha"
                    />
                  </div>
                ) : null}
                <Button
                  color="primary"
                  type="submit"
                  disabled={loading || !isValidEmail(email) || (!shouldByPassCaptcha && !recaptchaToken)}
                  className="broker-login-submit"
                >
                  {loading ? <FaSpinner className="broker-spin" aria-hidden="true" /> : null}
                  <span>{loading ? "Sending code" : "Continue"}</span>
                  {!loading ? <FaArrowRight aria-hidden="true" /> : null}
                </Button>
              </Form>
            ) : (
              <Form onSubmit={verifyOtp} aria-busy={loading}>
                <FormGroup className="broker-login-field">
                  <Label for="broker-email-readonly">Email address</Label>
                  <Input id="broker-email-readonly" type="email" value={email} disabled />
                </FormGroup>
                <div className="broker-login-otp">
                  <OtpInput
                    testId="broker-login-otp"
                    value={otpValue}
                    onChange={(value) => {
                      setOtpValue(value);
                      setPageError("");
                    }}
                  />
                </div>
                <div className="broker-login-actions">
                  <Button
                    color="light"
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      setStep("credentials");
                      setPageError("");
                      refreshCaptcha();
                    }}
                  >
                    <FaArrowLeft aria-hidden="true" /> Back
                  </Button>
                  <Button color="primary" type="submit" disabled={loading || otpValue?.length !== 4}>
                    {loading ? <FaSpinner className="broker-spin" aria-hidden="true" /> : null}
                    {loading ? "Signing in" : "Sign in"}
                  </Button>
                </div>
              </Form>
            )}
          </CardBody>
        </Card>
      </section>
    </main>
  );
};

export default BrokerLogin;
